import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

const HUDSON_YARDS_MAP_EMBED =
	"https://www.google.com/maps/d/embed?mid=1AOn9JPYZCFp_MB8w__92VLgRXS5LeIk&ehbc=2E312F";

// Template89: Día 6 Mapa Hudson Yards + West Village (Page20). Contenido desde page.
const Template89 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onMapFullscreenChange,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(navigator.onLine);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(navigator.onLine);

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const title = page?.texts?.[0]?.content;
	const yellowMapRaw = page?.texts?.[1]?.content;
	const yellowMapTitle = yellowMapRaw?.includes("\n")
		? yellowMapRaw.split("\n")[0]
		: yellowMapRaw;
	const yellowMapBody = yellowMapRaw?.includes("\n")
		? yellowMapRaw.split("\n").slice(1).join(" ").trim()
		: "";
	const westIntro = page?.texts?.[2]?.content;
	const westH3 = page?.texts?.[3]?.content;
	const westP = page?.texts?.[4]?.content;
	const bleeckerRaw = page?.texts?.[5]?.content;
	const bleeckerTitle = bleeckerRaw?.includes("\n")
		? bleeckerRaw.split("\n")[0]
		: bleeckerRaw;
	const bleeckerBody = bleeckerRaw?.includes("\n")
		? bleeckerRaw.split("\n").slice(1).join(" ").trim()
		: "";
	const localizacionesRaw = page?.texts?.[6]?.content;
	const localizacionesAll = localizacionesRaw
		? String(localizacionesRaw)
				.split("\n")
				.map((s) => s.trim())
				.filter(Boolean)
		: [];
	const localizacionesTitle = localizacionesAll[0] || "";
	const localizacionesItems = localizacionesAll.slice(1);

	const mapUrl = page?.maps?.[0] || HUDSON_YARDS_MAP_EMBED;
	const mapaOfflineSrc = getImageSrc("mapa-offline");
	const mapStickerSrc = getImageSrc("map-sticker");
	const westVillageSrc = getImageSrc("west-village");
	const westVillageStickerSrc = getImageSrc("west-village-sticker");
	const logoSrc = getCanvasImageUrl("logo");

	useEffect(() => {
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	useEffect(() => {
		const style = document.createElement("style");
		style.innerHTML = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;
		document.head.appendChild(style);
		return () => document.head.removeChild(style);
	}, []);

	if (fullscreen) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: "#59c3d8",
					}}
				>
					<Box
						component="button"
						onClick={() => {
							setFullscreen(false);
							onMapFullscreenChange?.(false);
						}}
						sx={{
							backgroundColor: "#fff",
							color: "#59c3d8",
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<Box
					component="iframe"
					title="Mapa grande Hudson Yards"
					src={mapUrl}
					width="100%"
					height="100%"
					sx={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	const estiloH3 = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};
	const estiloP = {
		fontFamily: '"Poppins", sans-serif',
		fontSize: 13,
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			<div data-block-id="map" style={{ marginTop: "-70px", position: "relative" }}>
				{!loaded && isOnline && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<div
							style={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: "4px solid #59c3d8",
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
							}}
						/>
					</div>
				)}
				{isOnline && (
					<Box
						component="button"
						onClick={() => {
							setFullscreen(true);
							onMapFullscreenChange?.(true);
						}}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: "#59c3d8",
							color: "#fff",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
							fontFamily: fontFamily || '"Poppins", sans-serif',
						}}
					>
						Ver mapa grande
					</Box>
				)}
				{isOnline ? (
					<Box
						component="iframe"
						title="Mapa Hudson Yards"
						src={mapUrl}
						width="100%"
						height="500px"
						sx={{ border: 0 }}
						loading="lazy"
						onLoad={() => setLoaded(true)}
					/>
				) : mapaOfflineSrc ? (
					<img
						src={mapaOfflineSrc}
						alt="mapa-offline"
						style={{
							width: "100%",
							height: "500px",
							objectFit: "cover",
							position: "relative",
						}}
					/>
				) : null}
				{mapStickerSrc && (
					<img
						src={mapStickerSrc}
						alt="hudson"
						style={{ width: 140, position: "absolute", bottom: -10, left: -18 }}
					/>
				)}
				{(yellowMapTitle || yellowMapBody) && (
					<div
						style={{
							position: "absolute",
							bottom: 110,
							left: 10,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: '"Poppins", sans-serif',
							color: "#444",
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 120,
						}}
					>
						{yellowMapTitle && <strong>{yellowMapTitle}</strong>}
						{yellowMapBody && (
							<>
								<br />
								{yellowMapBody}
							</>
						)}
					</div>
				)}
			</div>

			{westIntro && (
				<div
					data-block-id="text-1"
					style={{
						padding: "16px 12px",
						paddingBottom: 0,
						display: "flex",
						position: "relative",
					}}
				>
					<p style={{ ...estiloP, width: "100%" }}>{westIntro}</p>
				</div>
			)}

			{(westH3 || westP || westVillageSrc) && (
				<div
					data-block-id="text-2"
					style={{
						padding: "14px 12px",
						display: "flex",
						gap: 16,
						position: "relative",
					}}
				>
					{westVillageSrc && (
						<img
							src={westVillageSrc}
							alt="West Village"
							style={{
								width: "45%",
								borderRadius: 12,
								objectFit: "cover",
								height: 110,
							}}
						/>
					)}
					<div style={{ width: "60%" }}>
						{westH3 && <h3 style={estiloH3}>{westH3}</h3>}
						{westP && <p style={estiloP}>{westP}</p>}
					</div>
				</div>
			)}

			<div
				style={{ padding: "0px 12px", display: "flex", position: "relative" }}
			>
				{westVillageStickerSrc && (
					<img
						src={westVillageStickerSrc}
						alt="West Village"
						style={{
							width: isSmallScreen ? 100 : 112,
							position: "absolute",
							top: isSmallScreen ? 100 : -40,
							right: isSmallScreen ? -20 : -32,
						}}
					/>
				)}
				{(bleeckerTitle || bleeckerBody) && (
					<div
						data-block-id="text-3"
						style={{
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 0px 16px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: '"Poppins", sans-serif',
							color: "#444",
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: isSmallScreen ? 200 : 150,
							position: "absolute",
							top: isSmallScreen ? 70 : -50,
							right: isSmallScreen ? 50 : 50,
						}}
					>
						{bleeckerTitle && <strong>{bleeckerTitle}</strong>}
						{bleeckerBody && (
							<>
								<br />
								{bleeckerBody}
							</>
						)}
					</div>
				)}
				{(localizacionesTitle || localizacionesItems.length > 0) && (
					<div
						data-block-id="text-4"
						style={{
							...estiloP,
							width: "100%",
							marginBottom: isSmallScreen ? 100 : 16,
						}}
					>
						<strong>{localizacionesTitle}</strong>
						<br />
						{localizacionesItems.map((line, i) => (
							<React.Fragment key={i}>
								{line.startsWith("-") ? line : `- ${line}`}
								{i < localizacionesItems.length - 1 && <br />}
							</React.Fragment>
						))}
					</div>
				)}
			</div>
		</Box>
	);
};

export default Template89;
